/**
 *
 * @module userProfile
 * @desc userProfile sequence implementation
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/UserProfileApi.md
 *
 */

import Check from 'check-types';
import assert from 'assert';

import SDK from './../../sdk';
import BaseSequence from './../base';
import Exception from './../../services/exception/entry';

const InvalidArgumentException = Exception.InvalidArgumentException;

const {
    object,
    nonEmptyObject,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class UserProfileSequence
 * @extends {BaseSequence}
 *
 * @example const userProfileSequence = new SDK.Test.Sequences.UserProfile(config, options);
 *
 */
export default class UserProfileSequence extends BaseSequence {

    /**
     *
     * @param {Object} config configuration used to build a BootstrapConfiguration instance
     * @param {Object} options contains profile specfic params
     * @param {String} options.profileName the name of the profile to create
     * @throws {InvalidArgumentException}
     *
     */
    constructor(config, options) {

        const errorMessage = {
            ctor: 'UserProfileSequence(config, options)',
            get config() {
                return `${this.ctor} config should be a non-empty Object`;
            },
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get profileName() {
                return `${this.ctor} profileName should be a String with length`;
            }
        };

        object(config, errorMessage.config, InvalidArgumentException);
        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { profileName } = options;

        nonEmptyString(profileName, errorMessage.profileName, InvalidArgumentException);

        super(config);

        /**
         *
         * @access public
         * @type {Object}
         *
         */
        this.options = options;

    }

    /**
     *
     * @param {SDK.SdkSession} session
     * @param {String} endpoint the name of the endpoint to add the mock and auth headers for
     * @note The Authorization header was created using the instructions from
     * https://wiki.bamtechmedia.com/display/DEVOPS/Multiple+Profiles+Mocks
     * with 8ca3290d-bc08-4cc2-98f8-c59009b347a0 as the account id
     *
     */
    addMockHeaders(session, endpoint) {

        const endpoints = SDK.Services.Util.getSafe(() => session.accountManager.client.config.endpoints);

        endpoints[endpoint].headers.Authorization = 'Bearer eyJraWQiOiJlNzliMTEyYS1jZmExLTExZTctYWJjNC1jZWMyNzhiNmI1MGEiLCJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOiI3YTEzNGVlMy0xMTQyLTQ3MGUtYWM3Yi1lOGUzNDIwZDEzMTQiLCJuYmYiOjE1NDMzNjYyNTksInBhcnRuZXJOYW1lIjoiZXNwbiIsImlzcyI6InVybjpiYW10ZWNoOnNlcnZpY2U6dG9rZW4iLCJjb250ZXh0Ijp7InZlcnNpb24iOiJWMi4wLjAiLCJpZCI6ImFjNDk1ZjMwLWYyYTctMTFlOC1iMGIwLTAyNDJhYzExMDAwOSIsInBhcnRuZXIiOnsibmFtZSI6ImVzcG4ifSwidHlwZSI6IlJFR0lTVEVSRUQiLCJpcF9hZGRyZXNzIjoiMTM5LjEwNC4xOTIuMTAiLCJkZXZpY2UiOnsiaWQiOiI4ZDE3NzA0Mi0yMmQyLTQxMWQtOTkwMS1hMDY5YWJiYjI0MzAiLCJwbGF0Zm9ybSI6ImFwcGxlIn0sImxvY2F0aW9uIjp7InR5cGUiOiJaSVBfQ09ERSIsInppcF9jb2RlIjoiMTAxMCIsImNvdW50cnlfY29kZSI6Im5sIn0sInByb2ZpbGVzIjpbeyJhY3RpdmUiOnRydWUsInR5cGUiOiJ1cm46YmFtdGVjaDpwcm9maWxlIiwiaWQiOiI0NjYxOGEyNS0wOWQ1LTRhYmQtYWQ4My0wNWVkNGIzNjI3ZTYiLCJwYXJlbnRhbF9jb250cm9scyI6eyJlbmFibGVkIjpmYWxzZX19LHsiYWN0aXZlIjpmYWxzZSwidHlwZSI6InVybjpiYW10ZWNoOnByb2ZpbGUiLCJpZCI6IjdjNzI3MDk1LTA3OTEtNDI0YS05YmQ2LTM3OWI2YjI1NzIxYiIsInBhcmVudGFsX2NvbnRyb2xzIjp7ImVuYWJsZWQiOmZhbHNlfX1dLCJnZW5lcmF0ZWRfb24iOiIyMDE4LTExLTI4VDAwOjUwOjU5Ljg3NSswMDAwIiwidXBkYXRlZF9vbiI6IjIwMTgtMTEtMjhUMDA6NTA6NTkuODc1KzAwMDAiLCJleHBpcmVzX29uIjoiMjAxOC0xMS0yOFQwMTowMDo1OS44NzUrMDAwMCIsIm1lZGlhX3Blcm1pc3Npb25zIjp7ImVudGl0bGVtZW50cyI6WyJFU1BOX1BMVVMiXSwicnVsZXMiOnsicGFzc2VkIjpbXX0sImRhdGEiOnt9fSwiYmxhY2tvdXRzIjp7InJ1bGVzIjp7InZpb2xhdGVkIjpbXX0sImRhdGEiOnt9fSwiYWNjb3VudCI6eyJ0eXBlIjoidXJuOmJhbXRlY2g6YWNjb3VudCIsImlkIjoiOGNhMzI5MGQtYmMwOC00Y2MyLTk4ZjgtYzU5MDA5YjM0N2EwIiwiaG9tZV9sb2NhdGlvbiI6eyJ0eXBlIjoiVU5LTk9XTiJ9LCJkYXRhIjp7fX0sImxvZ2luX2NvbnRleHQiOnsiaWRwIjoiaHR0cHM6Ly9zdGcuYXV0aG9yaXphdGlvbi5nby5jb20iLCJhaWQiOiJ7QkVGMTQ3OTgtNzZCMS00QzM4LTkxOEUtQjQyMDdCMUIzMzZBfSJ9fSwic2Vzc2lvbklkIjoiYWM0OTVmMzAtZjJhNy0xMWU4LWIwYjAtMDI0MmFjMTEwMDA5IiwiZXhwIjoxNTQzMzY2ODU5LCJlbnYiOiJxYSIsImlhdCI6MTU0MzM2NjI1OSwianRpIjoiYWM0OTg2NDAtZjJhNy0xMWU4LWIwYjAtMDI0MmFjMTEwMDA5In0.5Dgxx8IZ_NDPpORisYvLI5pkFvCVP8FAs1SwzgvD2i8'; // eslint-disable-line
        endpoints[endpoint].headers['Mock-Magic-Value'] = 'persisted';

    }

    /**
     *
     * @access public
     * @method execute
     * @desc runs the sequence to completion
     *
     * @example userProfileSequence.execute().then(() => { console.log('success'); });
     *
     */
    execute() {

        const { config, options } = this;
        const { clientId, clientApiKey, environment, debugEnabled } = config;
        const { profileName } = options;

        const BootstrapConfiguration = SDK.Services.Configuration.BootstrapConfiguration;
        const UserProfile = SDK.Services.Account.UserProfile;

        return new Promise((resolve, reject) => {

            /** @type {SDK.Services.Configuration.BootstrapConfiguration} */
            const bootstrapConfiguration = new BootstrapConfiguration({
                clientId,
                clientApiKey,
                environment,
                debugEnabled
            });

            /** @type {SDK.SdkSession} */
            let sdkSession;

            /** @type {SDK.UserProfile.UserProfileApi} */
            let userProfileApi;

            /** @type {SDK.Services.Account.UserProfile} */
            let userProfile;

            SDK.SdkSession.createSdkSession(bootstrapConfiguration).then((_sdkSession) => {

                /** @type {SDK.SdkSession} sa */
                sdkSession = _sdkSession;

                /** @test */
                assert.ok(_sdkSession);

                /** Establish Anonymous Session */
                return sdkSession.initialize();

            }).then(() => {

                /** @type {SDK.UserProfile.UserProfileApi} */
                userProfileApi = sdkSession.userProfileApi;

                if (environment === 'mock') {
                    /** Add the mock and auth headers for each of the user profile methods being used below */
                    this.addMockHeaders(sdkSession, 'createUserProfile');
                    this.addMockHeaders(sdkSession, 'deleteUserProfile');
                    this.addMockHeaders(sdkSession, 'getUserProfile');
                    this.addMockHeaders(sdkSession, 'getUserProfiles');
                    this.addMockHeaders(sdkSession, 'setActiveUserProfile');
                    this.addMockHeaders(sdkSession, 'getActiveUserProfile');
                    this.addMockHeaders(sdkSession, 'getCurrentAccount');
                    this.addMockHeaders(sdkSession, 'updateUserProfile');
                }

                const attributes = {
                    languagePreferences: {
                        playbackLanguage: 'EN',
                        subtitleAppearance: {
                            backgroundColor: '#000',
                            size: 'small',
                            description: 'description',
                            backgroundOpacity: 2,
                            textColor: '#FFF',
                            font: 'Arial'
                        },
                        subtitleLanguage: 'EN',
                        appLanguage: 'EN'
                    },
                    parentalControls: {
                        tv: {
                            restrictions: {
                                contentMaturityRating: 'AL',
                                advisoryFilters: {
                                    drugs: true,
                                    badLanguage: true,
                                    discrimination: true,
                                    scary: true,
                                    sexual: true,
                                    violence: true
                                }
                            },
                            ratingSystem: 'kijkwijzer'
                        },
                        movie: {
                            restrictions: {
                                contentMaturityRating: 'AL',
                                advisoryFilters: {
                                    drugs: true,
                                    badLanguage: true,
                                    discrimination: true,
                                    scary: true,
                                    sexual: true,
                                    violence: true
                                }
                            },
                            ratingSystem: 'kijkwijzer'
                        }
                    },
                    playbackSettings: {
                        previewAudioOnHome: true,
                        previewVideoOnHome: true,
                        autoplay: true
                    }
                };

                const metadata = {
                    isTest: true
                };

                return userProfileApi.createUserProfile(profileName, attributes, metadata);

            }).then((_userProfile) => {

                /** @type {SDK.Services.Account.UserProfile} */
                userProfile = _userProfile;

                /** @test */
                assert.ok(userProfile instanceof UserProfile);

                return userProfileApi.setActiveUserProfile(userProfile);

            }).then(() => {

                return userProfileApi.getActiveUserProfile();

            }).then((_userProfile) => {

                /** @test */
                assert.equal(_userProfile.profileId, userProfile.profileId);

                return userProfileApi.getUserProfiles();

            }).then((_userProfiles) => { //eslint-disable-line

                /** @test */
                assert(_userProfiles.length >= 1);

                const updatedProfile = new UserProfile(userProfile);

                updatedProfile.attributes.playbackSettings.autoplay = false;
                updatedProfile.attributes.languagePreferences.subtitleLanguage = 'FR';
                updatedProfile.attributes.parentalControls.movie.restrictions.advisoryFilters.badLanguage = false;

                return userProfileApi.updateUserProfile(updatedProfile);

            }).then((_userProfile) => { //eslint-disable-line

                return userProfileApi.getUserProfile(userProfile.profileId);

            }).then((_userProfile) => {

                /** @test */
                assert.equal(_userProfile.attributes.playbackSettings.autoplay, false);
                assert.equal(_userProfile.attributes.languagePreferences.subtitleLanguage, 'FR');
                assert.equal(_userProfile.attributes.parentalControls.movie.restrictions.advisoryFilters.badLanguage, false); //eslint-disable-line

                return userProfileApi.deleteUserProfile(userProfile);

            }).then(() => {

                if (sdkSession.tokenManager) {
                    sdkSession.tokenManager.clearRetryRefresh();
                }

                return resolve();

            }).catch((ex) => {

                return reject(ex);

            });

        });

    }

}



// WEBPACK FOOTER //
// ./src/sequences/userProfile/userProfile.js